import {m} from 'framer-motion'
import {FC, PropsWithChildren, ReactNode} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'

const linkVariants = {
  rest: () => ({
    backgroundColor: 'transparent',
  }),
  hover: () => ({
    backgroundColor: 'transparent',
  }),
}

type UnderlinedButtonProps = {
  textVariant?: TextVariant
  fontWeight?: TextFontWeight
  onClick?: () => void
  textColor?: string
  underlineColor?: string
  icon?: ReactNode
  linkHref?: string
}

export const UnderlinedButton: FC<PropsWithChildren<UnderlinedButtonProps>> = ({
  textVariant,
  fontWeight,
  icon,
  onClick,
  textColor = theme.palette.text.primary,
  underlineColor = theme.palette.primary.main,
  linkHref,
  children,
}) => {
  const className = useUniqueClassName('underlined-button')
  const titleVariant = textVariant ?? TextVariant.text14
  const titleFontWeight = fontWeight ?? TextFontWeight.medium

  return (
    <>
      <m.span
        className={className}
        onClick={onClick}
        initial="rest"
        animate="rest"
        whileHover="hover"
        whileFocus="hover"
        variants={linkVariants}
        transition={{duration: HOVER_TRANSITION / 1000, ease: 'easeInOut'}}
      >
        <span className={`${className}-content`}>
          <Text
            variant={titleVariant}
            color={textColor}
            fontWeight={titleFontWeight}
            element="span"
          >
            <m.span
              className={`${className}-title`}
              variants={{
                rest: {backgroundSize: '8px 1px, 0% 1px'},
                hover: {backgroundSize: '0% 1px, 100% 1px'},
              }}
              transition={{duration: HOVER_TRANSITION / 1000, ease: 'easeInOut'}}
            >
              {linkHref ? <a href={linkHref}>{children}</a> : children}
            </m.span>
          </Text>
          {icon && <span className={`${className}-icon`}>{icon}</span>}
        </span>
      </m.span>

      <style jsx global>{`
        .${className} {
          display: inline-block;
          cursor: pointer;
        }
        .${className}-content {
          display: inline-flex;
          align-items: center;
          gap: ${theme.spacing[1.5]};
        }
        .${className}-title {
          display: inline;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
          background-image: repeating-linear-gradient(
              to right,
              ${underlineColor} 0,
              ${underlineColor} 4px,
              transparent 4px,
              transparent 8px
            ),
            linear-gradient(${underlineColor}, ${underlineColor});
          background-repeat: repeat-x, no-repeat;
          background-position: 0 100%, 0 100%;
          padding-bottom: ${theme.spacing[0.5]};
        }
      `}</style>
    </>
  )
}
